import React, { useState, useRef, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Tag } from 'primereact/tag';
import { Divider } from 'primereact/divider';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { classNames } from 'primereact/utils';
import axiosInstance from '../../../config/axios.config';
import { useReportService, useInvalidateReportService } from '../../../hooks/queries/useReportService';
import BankStatementOutputComponent from '../output/BankStatementOutputComponent';
import ConsentCheckbox from './ConsentCheckbox';
import ReportBanner from '../ReportBanner';

const ACCOUNT_TYPES = [
    { label: 'Savings', value: 'SAVING' },
    { label: 'Current', value: 'CURRENT' },
    { label: 'Salary', value: 'SALARY' },
    { label: 'Overdraft', value: 'OD' },
];

const PAN_REGEX = /^[A-Z]{5}\d{4}[A-Z]$/;
const MOBILE_REGEX = /^[6-9]\d{9}$/;
const NAME_REGEX = /^[A-Za-z\s]+$/;
const MAX_BYTES = 10 * 1024 * 1024;

const blankStatement = () => ({ bank: '', accountType: 'SAVING', files: [], password: '' });

// Month-name → index for chronological sorting of the date-wise breakdown.
const MONTH_INDEX = {
    jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5,
    jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11,
};

/**
 * Month labels in the provider's own "Oct 2025" form. Used to re-nest a merged
 * multi-account day-book back into the month→day shape the ABB calculator
 * reads, so MONTH_INDEX above can parse it straight back.
 */
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Parse an `abb_dates` value into a sorted array of day-numbers.
 * The rate-card CSV stores it as a quoted string ("1,5,15,25"); callers may
 * also pass an array. Anything outside 1..31 is dropped.
 *
 * An EMPTY result is meaningful, not an error: it means the rate card configures
 * no sampling dates, which selects the all-calendar-days fallback. Do NOT
 * substitute a default date set — that would show the DSA one lender's dates
 * while the offer engine averages every day.
 *
 * Mirrors AverageBankingBalanceMethodService::parseAbbDates() on the backend.
 */
export const parseAbbDates = (raw) => {
    if (raw === null || raw === undefined || raw === '') return [];
    const list = Array.isArray(raw) ? raw : String(raw).split(',');
    const days = list
        .map((d) => parseInt(String(d).trim(), 10))
        .filter((d) => Number.isFinite(d) && d >= 1 && d <= 31);
    return [...new Set(days)].sort((a, b) => a - b);
};

/**
 * Flatten the provider's month → day → entries map into the flat
 * `{ 'YYYY-MM-DD': closingBalance }` shape the backend's ABB calculator expects.
 *
 * The backend recomputes ABB per offer on each lender's own abb_dates, so it
 * needs the whole day-book — not the single pre-averaged number this component
 * shows as a preview. Without this the server falls back to a manually-entered
 * ABB and every lender's card reports an identical figure.
 *
 * Multiple entries can exist for one day; the LAST is the true end-of-day balance.
 */
export const flattenDailyBalances = (dailyEODBalance) => {
    if (!dailyEODBalance || typeof dailyEODBalance !== 'object') return {};
    const flat = {};
    Object.entries(dailyEODBalance).forEach(([monthLabel, days]) => {
        if (!days || typeof days !== 'object') return;
        const [mon, yr] = String(monthLabel).toLowerCase().split(' ');
        const monthIdx = MONTH_INDEX[mon?.slice(0, 3)];
        const year = parseInt(yr, 10);
        if (monthIdx === undefined || !Number.isFinite(year)) return;
        Object.entries(days).forEach(([day, list]) => {
            const dayNum = parseInt(day, 10);
            if (!Number.isFinite(dayNum)) return;
            const arr = Array.isArray(list) ? list : [list];
            const bal = Number(arr[arr.length - 1]?.closingBalance);
            if (!Number.isFinite(bal)) return;
            const key = `${year}-${String(monthIdx + 1).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`;
            flat[key] = bal;
        });
    });
    return flat;
};

/**
 * Merge every account's day-book into one combined balance-per-date series.
 *
 * The borrower may upload statements for several banks ("Add bank" above), and
 * their banking strength is the TOTAL across accounts — so balances are SUMMED
 * per date. Mirrors mergeAccountDayBooks() in LeadWorkflowController so this
 * preview and the server-side per-lender ABB agree.
 *
 * Accounts transact on different days, so an account that posted nothing on a
 * given date carries its last known balance forward rather than counting as
 * zero — otherwise the combined balance would collapse on exactly the days the
 * other bank happened to move. Before its first posting an account contributes
 * nothing, so a statement that starts later never invents money.
 *
 * @param {Object} summaries account_summaries keyed by accountUID
 * @returns {Object} { 'YYYY-MM-DD': combinedClosingBalance }
 */
export const mergeAccountDailyBalances = (summaries) => {
    if (!summaries || typeof summaries !== 'object') return {};

    const books = Object.values(summaries)
        .map((acc) => flattenDailyBalances(acc?.dailyEODBalance))
        .filter((b) => Object.keys(b).length > 0);

    if (books.length === 0) return {};
    if (books.length === 1) return books[0];

    const allDates = new Set();
    books.forEach((b) => Object.keys(b).forEach((d) => allDates.add(d)));

    const lastKnown = new Array(books.length).fill(null);
    const merged = {};

    Array.from(allDates).sort().forEach((date) => {
        let sum = 0;
        let anyKnown = false;
        books.forEach((book, i) => {
            if (Object.prototype.hasOwnProperty.call(book, date)) lastKnown[i] = book[date];
            if (lastKnown[i] !== null) { sum += lastKnown[i]; anyKnown = true; }
        });
        if (anyKnown) merged[date] = Math.round(sum * 100) / 100;
    });

    return merged;
};

/** Days in a given month, from the provider's "Feb 2025" style label. */
const daysInMonthLabel = (monthLabel) => {
    const [mon, yr] = String(monthLabel).toLowerCase().split(' ');
    const monthIdx = MONTH_INDEX[mon?.slice(0, 3)];
    const year = parseInt(yr, 10);
    if (monthIdx === undefined || !Number.isFinite(year)) return null;
    return new Date(year, monthIdx + 1, 0).getDate();
};

/**
 * Find the closing balance for `targetDay`, falling back to the nearest day
 * within ±2. Banks only post a balance on days that had a transaction, so an
 * exact hit on (say) the 25th is not guaranteed.
 *
 * Search order matches the backend's findClosestBalance() exactly — step out
 * one day at a time, checking BEHIND before AHEAD — so the frontend preview
 * and the server-side offer calculation agree on which balance was used.
 */
const pickBalanceForDay = (dayBalances, targetDay) => {
    if (Number.isFinite(dayBalances[targetDay])) {
        return { balance: dayBalances[targetDay], usedDay: targetDay, exact: true };
    }
    for (let offset = 1; offset <= 2; offset++) {
        if (Number.isFinite(dayBalances[targetDay - offset])) {
            return { balance: dayBalances[targetDay - offset], usedDay: targetDay - offset, exact: false };
        }
        if (Number.isFinite(dayBalances[targetDay + offset])) {
            return { balance: dayBalances[targetDay + offset], usedDay: targetDay + offset, exact: false };
        }
    }
    return null;
};

/**
 * Compute ABB (Average Bank Balance) from the statement's date-wise closing
 * balances, sampling only the days the lender's rate card asks for.
 *
 * This is a two-tier average, NOT a pooled mean of every day:
 *   1. For each month, pick the balance on each `abbDates` day (±2-day fallback).
 *   2. Monthly ABB = mean of that month's picked balances.
 *   3. Overall ABB = mean of the monthly ABBs.
 *
 * Sampling fixed days is what makes ABB hard to game — a borrower would have to
 * hold a balance on every sampling date, not just park funds before applying.
 *
 * Mirrors AverageBankingBalanceMethodService::calculateABBFromDailyBalances()
 * on the backend. Keep the two in sync: the backend recomputes ABB per offer
 * using each lender's own abb_dates, and a mismatch here shows the DSA one
 * number while the offer engine uses another.
 *
 * CAVEAT (matches backend behaviour): a month is divided by the number of dates
 * actually found, not by abbDates.length, and every month carries equal weight.
 * A partial month at either end of the statement can therefore swing the result
 * on as little as one data point — `monthly` in the return value exposes
 * `picked`/`expected` per month so callers can warn on incomplete coverage.
 *
 * `dailyEODBalance` shape (from BoostMoney):
 *   { "Dec 2025": { "1": [{ closingBalance, openingBalance }], "2": [...] }, ... }
 * Multiple entries can exist for a single day — we take the LAST one as the
 * true end-of-day balance.
 *
 * @param {object} dailyEODBalance Provider's month → day → entries map
 * @param {string|number[]} abbDates Sampling days, e.g. "1,5,15,25" or [1,5,15,25]
 * @returns {{average: number, entries: {date: string, balance: number}[],
 *            monthly: object[], abbDates: number[], monthsCount: number,
 *            hasPartialMonths: boolean} | null}
 */
const computeAbbFromDailyBalances = (dailyEODBalance, abbDates = null) => {
    if (!dailyEODBalance || typeof dailyEODBalance !== 'object') return null;

    const targetDays = parseAbbDates(abbDates);

    // Group into month → { dayNumber: closingBalance }, keeping the LAST entry
    // of each day as the end-of-day balance.
    const months = [];
    Object.entries(dailyEODBalance).forEach(([monthLabel, days]) => {
        if (!days || typeof days !== 'object') return;
        const dayBalances = {};
        Object.entries(days).forEach(([day, list]) => {
            const dayNum = parseInt(day, 10);
            if (!Number.isFinite(dayNum)) return;
            const arr = Array.isArray(list) ? list : [list];
            const last = arr[arr.length - 1];
            const bal = Number(last?.closingBalance);
            if (!Number.isFinite(bal)) return;
            dayBalances[dayNum] = bal;
        });
        if (Object.keys(dayBalances).length === 0) return;

        const [mon, yr] = monthLabel.toLowerCase().split(' ');
        months.push({
            monthLabel,
            sortKey: (parseInt(yr, 10) || 0) * 12 + (MONTH_INDEX[mon?.slice(0, 3)] ?? 0),
            dayBalances,
        });
    });
    if (months.length === 0) return null;

    months.sort((a, b) => a.sortKey - b.sortKey);

    // No sampling dates on the rate card → average every calendar day instead.
    if (targetDays.length === 0) {
        return computeAbbOverAllCalendarDays(months);
    }

    // Tier 1 — per-month average over the sampled days.
    const monthly = [];
    const entries = [];
    months.forEach(({ monthLabel, dayBalances }) => {
        const picked = [];
        targetDays.forEach((targetDay) => {
            const hit = pickBalanceForDay(dayBalances, targetDay);
            if (!hit) return; // date not covered by the statement — skip it
            picked.push(hit.balance);
            entries.push({
                date: `${hit.usedDay} ${monthLabel}`,
                balance: hit.balance,
                targetDay,
                substituted: !hit.exact,
            });
        });
        if (picked.length === 0) return; // no sampled day landed in this month

        monthly.push({
            month: monthLabel,
            picked: picked.length,
            expected: targetDays.length,
            complete: picked.length === targetDays.length,
            monthlyAbb: picked.reduce((a, b) => a + b, 0) / picked.length,
        });
    });
    if (monthly.length === 0) return null;

    // Tier 2 — overall ABB is the mean of the MONTHLY averages, so each month
    // counts once regardless of how many days it contributed.
    const average = monthly.reduce((acc, m) => acc + m.monthlyAbb, 0) / monthly.length;

    return {
        average,
        entries,
        monthly,
        abbDates: targetDays,
        monthsCount: monthly.length,
        hasPartialMonths: monthly.some((m) => !m.complete),
        mode: 'selected_dates',
    };
};

/**
 * DEFAULT FALLBACK — ABB over every calendar day, used when the rate card
 * configures no abb_dates.
 *
 *     ABB = Sum of all daily closing balances ÷ Total number of calendar days
 *
 * A POOLED average across the whole period, not a mean of monthly means: every
 * day carries equal weight, so a 31-day month contributes 31 days and a 28-day
 * month 28. This deliberately differs from the selected-dates mode above, which
 * weights each month equally.
 *
 * Days with no posted balance (nothing moved that day) inherit the previous
 * day's closing balance — an account holds a balance every calendar day, and
 * counting only active days would bias ABB toward transaction days.
 *
 * Each month spans day 1 to its true last day, except that the first and last
 * months are clipped to the days the statement actually covers, so a
 * mid-month statement start doesn't invent balances for days before it.
 *
 * Mirrors AverageBankingBalanceMethodService::calculateABBOverAllCalendarDays().
 *
 * @param {{monthLabel: string, dayBalances: object}[]} months Chronologically sorted
 */
const computeAbbOverAllCalendarDays = (months) => {
    let totalSum = 0;
    let totalDays = 0;
    let carriedForwardDays = 0;
    let lastKnown = null;
    const monthly = [];
    const entries = [];

    months.forEach(({ monthLabel, dayBalances }) => {
        const observed = Object.keys(dayBalances).map(Number).sort((a, b) => a - b);
        if (observed.length === 0) return;

        const monthLength = daysInMonthLabel(monthLabel) ?? observed[observed.length - 1];
        // Clip the first month to its first observed day and the last month to its
        // last, so partial months at either end don't fabricate coverage.
        const isFirst = monthly.length === 0;
        const isLast = months[months.length - 1].monthLabel === monthLabel;
        const from = isFirst ? observed[0] : 1;
        const to = isLast ? observed[observed.length - 1] : monthLength;

        let monthSum = 0;
        let monthDays = 0;

        for (let day = from; day <= to; day++) {
            if (Number.isFinite(dayBalances[day])) {
                lastKnown = dayBalances[day];
            } else {
                carriedForwardDays++;
            }
            if (lastKnown === null) continue;

            monthSum += lastKnown;
            monthDays++;
            entries.push({ date: `${day} ${monthLabel}`, balance: lastKnown });
        }

        if (monthDays === 0) return;

        monthly.push({
            month: monthLabel,
            picked: monthDays,
            expected: monthLength,
            complete: monthDays === monthLength,
            monthlyAbb: monthSum / monthDays,
        });

        totalSum += monthSum;
        totalDays += monthDays;
    });

    if (totalDays === 0) return null;

    return {
        average: totalSum / totalDays,   // pooled, NOT the mean of monthly.monthlyAbb
        entries,
        monthly,
        abbDates: [],
        monthsCount: monthly.length,
        hasPartialMonths: monthly.some((m) => !m.complete),
        mode: 'all_calendar_days',
        totalCalendarDays: totalDays,
        carriedForwardDays,
    };
};

const BankStatementInputComponent = ({
    serviceId: serviceIdProp,
    onAnalysisComplete,
    // Sampling days from the lender's rate card (`abb_dates`, e.g. "1,5,15,25").
    // Omitted on the standalone /reports route, where no product is in context —
    // the default 5,10,15,25 then applies, same as the backend's fallback.
    // Null/absent (e.g. the standalone /reports route, where no product is in
    // context) means no configured dates → all-calendar-days fallback.
    abbDates = null,
    // 'ranges' when the days came from the product's rate card, anything else
    // (or absent) means the default is in play. Surfaced in the breakdown text
    // so a wrong ABB is immediately traceable to the wrong sampling days.
    abbDatesSource = null,
    // A previously-fetched report, held by the parent so the result survives
    // closing and reopening the drawer. Analysis costs credits, so re-running
    // it just because a tab was closed would charge the tenant twice.
    initialReportData = null,
    // Fires with the report on a successful fetch, and with null when the user
    // chooses "Analyse another" — lets the parent keep its copy in step.
    onReportChange,
} = {}) => {
    // Standalone route (/reports/bank-statement-analysis/:serviceId) supplies
    // serviceId via the URL. When mounted inside the Lead Wizard drawer there is
    // no route param, so the caller passes it as a prop instead.
    const { serviceId: serviceIdParam } = useParams();
    const serviceId = serviceIdProp || serviceIdParam;
    const toast = useRef(null);
    const submittingRef = useRef(false);

    const [formData, setFormData] = useState({
        first_name: '', phone: '', pan: '',
        loan_amount: '', interest_rate: '', period: '',
        consent: false,
    });
    const [statements, setStatements] = useState([blankStatement()]);
    const [errors, setErrors] = useState({});
    const [stmtErrors, setStmtErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [reportData, setReportData] = useState(initialReportData || null);
    // #161: one shared, cached query instead of a per-component fetch on every
    // mount. Concurrent consumers are deduped into a single request and a
    // revisit is served from cache; the balance is invalidated after a report
    // runs (below), which is what keeps a cached credit figure correct.
    const {
        data: fetchedServiceDetails,
        isFetching: fetchingService,
        isError: serviceError,
    } = useReportService(serviceId);
    const invalidateReportService = useInvalidateReportService();
    const serviceDetails = serviceError
        ? { available_credits: 'N/A', credits_required: 'N/A' }
        : (fetchedServiceDetails || null);

    // Auto-calculate EMI from loan amount + rate + period
    const emi = useMemo(() => {
        const P = parseFloat(formData.loan_amount);
        const annualR = parseFloat(formData.interest_rate);
        const n = parseInt(formData.period, 10);
        if (!P || !annualR || !n || P <= 0 || annualR <= 0 || n <= 0) return '';
        const r = annualR / 12 / 100;
        const num = P * r * Math.pow(1 + r, n);
        const den = Math.pow(1 + r, n) - 1;
        if (den === 0) return '';
        return Math.round(num / den).toString();
    }, [formData.loan_amount, formData.interest_rate, formData.period]);

    // The output component reads its data from `analysisResults` (analysisResults.account_summaries)
    // and gates its empty state on it. The upload response wraps the analysis payload in an envelope
    // ({ success, data: {...} }), so unwrap it the same way the Report History flow does
    // (see reportTypeMapping.js 'BANK_STATEMENT') before handing it to the output component.
    const analysisResults = useMemo(() => {
        if (!reportData) return null;
        return reportData.report_json || reportData.api_response || reportData.data || reportData;
    }, [reportData]);

    const loanDetails = useMemo(() => {
        const src = analysisResults || {};
        return src.loan_details || src.metadata?.loan_details || {
            loan_amount: formData.loan_amount ? Number(formData.loan_amount) : 0,
            interest_rate: formData.interest_rate ? Number(formData.interest_rate) : 0,
            period: formData.period ? Number(formData.period) : 0,
            emi: emi ? Number(emi) : 0,
        };
    }, [analysisResults, formData.loan_amount, formData.interest_rate, formData.period, emi]);

    const setField = (field, transform = (v) => v) => (e) => {
        setFormData((prev) => ({ ...prev, [field]: transform(e.target.value || '') }));
        if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
    };

    const setStmtField = (idx, key) => (e) => {
        const value = e.target ? e.target.value : e.value;
        setStatements((prev) => prev.map((s, i) => (i === idx ? { ...s, [key]: value } : s)));
        const errKey = `stmt-${idx}-${key}`;
        if (stmtErrors[errKey]) setStmtErrors((prev) => ({ ...prev, [errKey]: '' }));
    };

    const setStmtFiles = (idx) => (e) => {
        const files = Array.from(e.target.files || []);
        setStatements((prev) => prev.map((s, i) => (i === idx ? { ...s, files } : s)));
        const errKey = `stmt-${idx}-files`;
        if (stmtErrors[errKey]) setStmtErrors((prev) => ({ ...prev, [errKey]: '' }));
    };

    const addStatement = () => setStatements((prev) => [...prev, blankStatement()]);
    const removeStatement = (idx) => setStatements((prev) => prev.filter((_, i) => i !== idx));

    const validate = () => {
        const next = {};
        const stmtNext = {};
        if (!formData.first_name.trim()) next.first_name = 'Borrower name is required';
        else if (!NAME_REGEX.test(formData.first_name)) next.first_name = 'Letters and spaces only';
        if (!formData.phone) next.phone = 'Mobile number is required';
        else if (!MOBILE_REGEX.test(formData.phone)) next.phone = 'Enter a valid 10-digit Indian mobile';
        if (formData.pan && !PAN_REGEX.test(formData.pan)) next.pan = 'Invalid PAN format';

        statements.forEach((s, i) => {
            if (!s.bank.trim()) stmtNext[`stmt-${i}-bank`] = 'Bank name is required';
            if (!s.accountType) stmtNext[`stmt-${i}-accountType`] = 'Account type is required';
            if (!s.files || s.files.length === 0) stmtNext[`stmt-${i}-files`] = 'At least one PDF is required';
            else {
                const tooBig = s.files.find((f) => f.size > MAX_BYTES);
                if (tooBig) stmtNext[`stmt-${i}-files`] = `${tooBig.name} exceeds 10 MB`;
            }
        });

        if (!formData.consent) next.consent = 'Please confirm consent before submitting';
        return { next, stmtNext };
    };

    const submit = async () => {
        setLoading(true);
        try {
            const fd = new FormData();
            fd.append('first_name', formData.first_name.trim());
            fd.append('phone', formData.phone);
            if (formData.pan) fd.append('pan', formData.pan);
            fd.append('service_id', String(serviceId));
            fd.append('api_provider_service_id', String(parseInt(serviceDetails?.id || serviceId, 10)));
            if (formData.loan_amount) fd.append('loan_amount', formData.loan_amount);
            if (formData.interest_rate) fd.append('interest_rate', formData.interest_rate);
            if (formData.period) fd.append('period', formData.period);
            if (emi) fd.append('emi', emi);

            statements.forEach((s, i) => {
                fd.append(`statements[${i}][bank]`, s.bank.trim());
                fd.append(`statements[${i}][accountType]`, s.accountType);
                if (s.password) fd.append(`statements[${i}][password]`, s.password);
                s.files.forEach((file, fi) => {
                    if (fi === 0) fd.append(`statements[${i}][bankStmt]`, file);
                    else fd.append(`statements[${i}][bankStmt_${fi}]`, file);
                });
            });

            const res = await axiosInstance.post('/boostmoney/upload-multiple-statements', fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            if (!res.data.success) throw new Error(res.data.message || 'Bank statement analysis failed');
            setReportData(res.data);
            // Hand the report up so it survives the drawer being closed.
            onReportChange?.(res.data);
            toast.current?.show({ severity: 'success', summary: 'Statements analyzed', detail: 'Report ready below', life: 2000 });

            // Surface headline metrics to the parent (e.g. the Lead Wizard) so
            // it can prefill form fields. ABB (average banking balance) is the
            // reliable bank-statement metric for this product; also expose the
            // credit/debit totals for the parent to use as it sees fit.
            try {
                const summaries = res.data?.data?.account_summaries || {};
                const firstUid = Object.keys(summaries)[0];
                const summary = summaries[firstUid] || {};
                const mwa = summary.monthWiseAnalysis || {};
                // Borrower name: prefer the name typed on this form; fall back to
                // the statement's account-holder name if present.
                const holderName = summary.bank_account?.name
                    || summary.bank_account?.accountHolderName
                    || '';
                // ABB from the statement's date-wise closing balances (per the CSV
                // dates). Fall back to the provider's monthly average only if the
                // daily balances aren't available.
                //
                // Built from ALL uploaded accounts, not just the first: balances
                // are summed per date so the preview reflects total liquidity and
                // matches what the server prices offers on. computeAbb... wants
                // the provider's nested month→day shape, so the merged flat
                // day-book is re-nested here rather than duplicating its logic.
                const mergedBook = mergeAccountDailyBalances(summaries);
                const mergedNested = {};
                Object.entries(mergedBook).forEach(([iso, bal]) => {
                    const d = new Date(`${iso}T00:00:00`);
                    if (Number.isNaN(d.getTime())) return;
                    const label = `${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`;
                    if (!mergedNested[label]) mergedNested[label] = {};
                    mergedNested[label][String(d.getDate())] = [{ closingBalance: bal }];
                });

                const abbCalc = computeAbbFromDailyBalances(mergedNested, abbDates);
                const abbValue = abbCalc ? abbCalc.average : (mwa.monthlyAvgBal?.average || 0);
                onAnalysisComplete?.({
                    abb: abbValue,
                    abbBreakdown: abbCalc
                        ? {
                            method: abbCalc.mode === 'all_calendar_days'
                                ? `Daily closing balance of every calendar day`
                                    + ` (no ABB dates on the rate card)`
                                    + ` — ₹ total ÷ ${abbCalc.totalCalendarDays} days`
                                    + `, across ${abbCalc.monthsCount} month(s)`
                                : `Sampled on day ${abbCalc.abbDates.join(', ')} of each month`
                                    + ` (${abbDatesSource === 'ranges' ? "from lender's rate card" : 'system default'})`
                                    + ` — average of ${abbCalc.monthsCount} monthly average(s)`,
                            count: abbCalc.entries.length,
                            average: abbCalc.average,
                            dates: abbCalc.entries,
                            monthly: abbCalc.monthly,
                            abbDates: abbCalc.abbDates,
                            monthsCount: abbCalc.monthsCount,
                            hasPartialMonths: abbCalc.hasPartialMonths,
                            mode: abbCalc.mode,
                            totalCalendarDays: abbCalc.totalCalendarDays,
                            carriedForwardDays: abbCalc.carriedForwardDays,
                        }
                        : {
                            method: "Provider's monthly average balance (date-wise balances unavailable)",
                            count: 0,
                            average: abbValue,
                            dates: [],
                        },
                    // The full day-book, for the backend to re-average on each
                    // lender's own abb_dates. `abb` above is only this screen's
                    // preview, computed on the product's most common dates.
                    dailyBalances: flattenDailyBalances(summary.dailyEODBalance),
                    bankAccount: summary.bank_account || null,
                    totalCredits: mwa.credits?.totalValue || 0,
                    totalDebits: mwa.debits?.totalValue || 0,
                    fullName: (formData.first_name || '').trim() || holderName,
                    reportId: res.data?.report_id || null,
                });
            } catch (cbErr) {
                console.error('onAnalysisComplete callback failed:', cbErr?.message || cbErr);
            }
            // Credits just changed. Invalidating updates every mounted consumer at
            // once and is ordered strictly after the deduction.
            invalidateReportService(serviceId);
        } catch (error) {
            console.error('Bank statement analysis failed:', error?.message || 'Unknown error');
            const status = error.response?.status;
            const map = {
                500: 'Internal server error. Please try again later.',
                422: 'Please check your inputs and try again.',
                429: 'Too many requests. Please wait a minute before trying again.',
                400: 'Invalid input or PDF format.',
                413: 'One of the PDFs exceeds the size limit.',
            };
            let detail = map[status] || error.response?.data?.message || error.message || 'Analysis failed';
            if (status === 402) {
                const c = error.response?.data || {};
                detail = `Insufficient credits. Required: ${c.required_credits ?? 'N/A'}, Available: ${c.current_balance ?? 'N/A'}`;
            }
            toast.current?.show({ severity: 'error', summary: 'Analysis failed', detail, life: 3500 });
        } finally {
            setLoading(false);
            submittingRef.current = false;
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (submittingRef.current) return;
        const { next, stmtNext } = validate();
        if (Object.keys(next).length > 0 || Object.keys(stmtNext).length > 0) {
            setErrors(next);
            setStmtErrors(stmtNext);
            toast.current?.show({ severity: 'warn', summary: 'Validation error', detail: 'Please fix the highlighted fields', life: 3000 });
            return;
        }
        setErrors({});
        setStmtErrors({});
        submittingRef.current = true;
        const required = serviceDetails?.credits_required;
        const available = serviceDetails?.available_credits;
        if (typeof available === 'number' && typeof required === 'number' && available < required) {
            submittingRef.current = false;
            toast.current?.show({ severity: 'error', summary: 'Insufficient credits', detail: `Required: ${required}, Available: ${available}`, life: 3500 });
            return;
        }
        const totalFiles = statements.reduce((acc, s) => acc + (s.files?.length || 0), 0);
        confirmDialog({
            // Scoped to this component's own <ConfirmDialog group=...>. Without
            // the group, the global confirmDialog() also renders into any other
            // <ConfirmDialog /> mounted in the tree (e.g. the Lead Wizard's),
            // making the dialog appear twice when used inside the wizard drawer.
            group: 'bank-statement-confirm',
            message: (
                <div className="text-sm">
                    <div className="mb-2"><span className="text-600">Borrower:</span> <span className="text-900 font-medium">{formData.first_name}</span></div>
                    <div className="mb-2"><span className="text-600">Mobile:</span> <span className="text-900 font-medium">{formData.phone}</span></div>
                    <div className="mb-2"><span className="text-600">Statements:</span> <span className="text-900 font-medium">{statements.length} bank{statements.length > 1 ? 's' : ''}, {totalFiles} PDF{totalFiles !== 1 ? 's' : ''}</span></div>
                    {emi && <div className="mb-2"><span className="text-600">EMI:</span> <span className="text-900 font-medium">₹{Number(emi).toLocaleString('en-IN')}</span></div>}
                    <div className="mb-2"><span className="text-600">Available credits:</span> <span className="text-900 font-medium">{available ?? 'N/A'}</span></div>
                    <div><span className="text-600">Credits to deduct:</span> <span className="text-900 font-medium">{required ?? 'N/A'}</span></div>
                </div>
            ),
            header: 'Confirm bank statement analysis',
            icon: 'pi pi-question-circle text-primary pi-2x',
            acceptLabel: 'Yes, analyze',
            rejectLabel: 'Cancel',
            accept: submit,
            reject: () => { submittingRef.current = false; },
            style: { width: '90vw', maxWidth: '500px' },
            draggable: false,
        });
    };

    const handleAnother = () => {
        setReportData(null);
        // Clear the parent's copy too, else the old report would reappear the
        // next time the drawer is reopened.
        onReportChange?.(null);
        setFormData({ first_name: '', phone: '', pan: '', loan_amount: '', interest_rate: '', period: '', consent: false });
        setStatements([blankStatement()]);
        setErrors({});
        setStmtErrors({});
    };

    const fieldClass = (f) => classNames('w-full p-inputtext-sm', { 'p-invalid': !!errors[f] });
    const stmtClass = (k) => classNames('w-full p-inputtext-sm', { 'p-invalid': !!stmtErrors[k] });
    const errTip = (f, helpId) => errors[f] && (
        <small id={helpId} className="p-error block mt-1" role="alert"><i className="pi pi-exclamation-circle mr-1" aria-hidden="true" />{errors[f]}</small>
    );
    const stmtTip = (k, helpId) => stmtErrors[k] && (
        <small id={helpId} className="p-error block mt-1" role="alert"><i className="pi pi-exclamation-circle mr-1" aria-hidden="true" />{stmtErrors[k]}</small>
    );

    return (
        <>
            <Toast ref={toast} />
            <ConfirmDialog group="bank-statement-confirm" />
            <div className="grid">
                <div className="col-12">
                    <ReportBanner
                        icon="pi-wallet"
                        title="Bank Statement Analysis"
                        subtitle="Upload bank statement PDFs (one or more banks) for income / EMI analysis"
                        availableCredits={serviceDetails?.available_credits}
                        requiredCredits={serviceDetails?.credits_required}
                        loading={fetchingService}
                    />

                    {!reportData ? (
                        <Card>
                            <form onSubmit={handleSubmit} noValidate>
                                {/* Borrower details */}
                                <div className="flex align-items-center gap-2 mb-3">
                                    <i className="pi pi-user text-primary" aria-hidden="true" />
                                    <span className="text-sm font-semibold text-900 uppercase">Borrower Details</span>
                                </div>
                                <div className="grid">
                                    <div className="field col-12 md:col-6">
                                        <label htmlFor="bs-name" className="block font-medium text-sm mb-2">Borrower Name <span className="text-red-500">*</span></label>
                                        <IconField iconPosition="left" className="w-full">
                                            <InputIcon className="pi pi-user" />
                                            <InputText id="bs-name" value={formData.first_name} onChange={setField('first_name')} placeholder="Enter borrower name" maxLength={100} autoCapitalize="words" disabled={loading} className={fieldClass('first_name')} aria-invalid={!!errors.first_name} aria-describedby="bs-name-help" />
                                        </IconField>
                                        {errTip('first_name', 'bs-name-help')}
                                    </div>
                                    <div className="field col-12 md:col-6">
                                        <label htmlFor="bs-phone" className="block font-medium text-sm mb-2">Mobile Number <span className="text-red-500">*</span></label>
                                        <IconField iconPosition="left" className="w-full">
                                            <InputIcon className="pi pi-phone" />
                                            <InputText id="bs-phone" value={formData.phone} onChange={setField('phone', (v) => v.replace(/\D/g, '').slice(0, 10))} placeholder="9876543210" maxLength={10} inputMode="tel" disabled={loading} className={classNames(fieldClass('phone'), 'font-mono')} aria-invalid={!!errors.phone} aria-describedby="bs-phone-help" />
                                        </IconField>
                                        {errTip('phone', 'bs-phone-help')}
                                    </div>
                                    <div className="field col-12 md:col-6 lg:col-4">
                                        <label htmlFor="bs-pan" className="block font-medium text-sm mb-2">PAN (optional)</label>
                                        <IconField iconPosition="left" className="w-full">
                                            <InputIcon className="pi pi-id-card" />
                                            <InputText id="bs-pan" value={formData.pan} onChange={setField('pan', (v) => v.toUpperCase().trim().slice(0, 10))} placeholder="ABCDE1234F" maxLength={10} autoCapitalize="characters" spellCheck={false} disabled={loading} className={classNames(fieldClass('pan'), 'font-mono')} aria-invalid={!!errors.pan} aria-describedby="bs-pan-help" />
                                        </IconField>
                                        {errTip('pan', 'bs-pan-help')}
                                    </div>
                                </div>

                                {/* Loan parameters (optional) */}
                                <Divider />
                                <div className="flex align-items-center gap-2 mb-3">
                                    <i className="pi pi-calculator text-primary" aria-hidden="true" />
                                    <span className="text-sm font-semibold text-900 uppercase">Loan Parameters (optional)</span>
                                </div>
                                <div className="grid">
                                    <div className="field col-12 md:col-3">
                                        <label htmlFor="bs-amount" className="block font-medium text-sm mb-2">Loan Amount (₹)</label>
                                        <InputText id="bs-amount" value={formData.loan_amount} onChange={setField('loan_amount', (v) => v.replace(/\D/g, ''))} placeholder="500000" maxLength={10} inputMode="numeric" disabled={loading} className="w-full p-inputtext-sm font-mono" />
                                    </div>
                                    <div className="field col-12 md:col-3">
                                        <label htmlFor="bs-rate" className="block font-medium text-sm mb-2">Interest Rate (% p.a.)</label>
                                        <InputText id="bs-rate" value={formData.interest_rate} onChange={setField('interest_rate', (v) => v.replace(/[^\d.]/g, ''))} placeholder="10.5" maxLength={5} inputMode="decimal" disabled={loading} className="w-full p-inputtext-sm font-mono" />
                                    </div>
                                    <div className="field col-12 md:col-3">
                                        <label htmlFor="bs-period" className="block font-medium text-sm mb-2">Period (months)</label>
                                        <InputText id="bs-period" value={formData.period} onChange={setField('period', (v) => v.replace(/\D/g, '').slice(0, 3))} placeholder="60" maxLength={3} inputMode="numeric" disabled={loading} className="w-full p-inputtext-sm font-mono" />
                                    </div>
                                    <div className="field col-12 md:col-3 flex flex-column">
                                        <span className="text-sm font-medium mb-2">EMI (calculated)</span>
                                        <Tag severity={emi ? 'success' : 'secondary'} value={emi ? `₹${Number(emi).toLocaleString('en-IN')}/mo` : 'Fill loan params'} className="text-sm" />
                                    </div>
                                </div>

                                {/* Bank statements */}
                                <Divider />
                                <div className="flex align-items-center justify-content-between mb-3 gap-2">
                                    <div className="flex align-items-center gap-2">
                                        <i className="pi pi-wallet text-primary" aria-hidden="true" />
                                        <span className="text-sm font-semibold text-900 uppercase">Bank Statements ({statements.length})</span>
                                    </div>
                                    <Button type="button" label="Add bank" icon="pi pi-plus" size="small" outlined onClick={addStatement} disabled={loading} />
                                </div>
                                {statements.map((s, i) => {
                                    const fileInputId = `bs-files-${i}`;
                                    const fk = `stmt-${i}-files`;
                                    return (
                                        <div key={i} className="surface-100 border-round p-3 mb-3">
                                            <div className="flex justify-content-between align-items-center mb-3">
                                                <span className="text-sm font-semibold text-900">Bank {i + 1}</span>
                                                {statements.length > 1 && (
                                                    <Button type="button" icon="pi pi-trash" size="small" severity="danger" outlined aria-label={`Remove bank ${i + 1}`} onClick={() => removeStatement(i)} disabled={loading} />
                                                )}
                                            </div>
                                            <div className="grid">
                                                <div className="field col-12 md:col-6">
                                                    <label htmlFor={`bs-bank-${i}`} className="block font-medium text-sm mb-2">Bank Name <span className="text-red-500">*</span></label>
                                                    <IconField iconPosition="left" className="w-full">
                                                        <InputIcon className="pi pi-building" />
                                                        <InputText id={`bs-bank-${i}`} value={s.bank} onChange={setStmtField(i, 'bank')} placeholder="e.g. HDFC Bank" maxLength={50} disabled={loading} className={stmtClass(`stmt-${i}-bank`)} aria-invalid={!!stmtErrors[`stmt-${i}-bank`]} />
                                                    </IconField>
                                                    {stmtTip(`stmt-${i}-bank`, `bs-bank-${i}-help`)}
                                                </div>
                                                <div className="field col-12 md:col-3">
                                                    <label htmlFor={`bs-acct-${i}`} className="block font-medium text-sm mb-2">Account Type <span className="text-red-500">*</span></label>
                                                    <Dropdown id={`bs-acct-${i}`} value={s.accountType} options={ACCOUNT_TYPES} optionLabel="label" optionValue="value" onChange={setStmtField(i, 'accountType')} disabled={loading} className={stmtClass(`stmt-${i}-accountType`)} />
                                                </div>
                                                <div className="field col-12 md:col-3">
                                                    <label htmlFor={`bs-pwd-${i}`} className="block font-medium text-sm mb-2">PDF Password (if any)</label>
                                                    <IconField iconPosition="left" className="w-full">
                                                        <InputIcon className="pi pi-lock" />
                                                        <InputText id={`bs-pwd-${i}`} value={s.password} onChange={setStmtField(i, 'password')} placeholder="Optional" maxLength={50} type="password" disabled={loading} className="w-full p-inputtext-sm" />
                                                    </IconField>
                                                </div>
                                                <div className="field col-12">
                                                    <label htmlFor={fileInputId} className="block font-medium text-sm mb-2">Statement PDFs <span className="text-red-500">*</span></label>
                                                    <input id={fileInputId} type="file" accept="application/pdf" multiple disabled={loading} onChange={setStmtFiles(i)} aria-describedby={`${fileInputId}-help`} aria-invalid={!!stmtErrors[fk]} className="w-full" />
                                                    {s.files?.length > 0 && (
                                                        <div className="flex flex-wrap gap-1 mt-2">
                                                            {s.files.map((f, fi) => (
                                                                <Tag key={fi} severity="info" value={`${f.name} · ${(f.size / 1024).toFixed(0)} KB`} className="text-xs" />
                                                            ))}
                                                        </div>
                                                    )}
                                                    {stmtTip(fk, `${fileInputId}-help`)}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}

                                <ConsentCheckbox
                                    checked={formData.consent}
                                    onChange={(e) => { setFormData((p) => ({ ...p, consent: e.checked })); setErrors((p) => ({ ...p, consent: '' })); }}
                                    error={errors.consent}
                                    reportType="bank-statement"
                                />
                                <div className="flex justify-content-end mt-3">
                                    <Button type="submit" label="Analyze Statements" icon="pi pi-arrow-right" iconPos="right" loading={loading} disabled={loading} size="small" />
                                </div>
                            </form>
                        </Card>
                    ) : (
                        <>
                            <div className="flex justify-content-end mb-3">
                                <Button label="Analyze another" icon="pi pi-refresh" severity="secondary" outlined size="small" onClick={handleAnother} />
                            </div>
                            <BankStatementOutputComponent
                                // Output component reads the analysis from the analysisResults
                                // prop (analysisResults.account_summaries) and gates its empty
                                // state on it. `analysisResults`/`loanDetails` memos above unwrap
                                // the upload envelope (report_json || api_response || data ||
                                // reportData) so it works for both the upload response and a
                                // rehydrated report. Without this it shows "No Analysis Data".
                                analysisResults={analysisResults}
                                loanDetails={loanDetails}
                                reportData={analysisResults}
                                serviceDetails={serviceDetails}
                            />
                        </>
                    )}
                </div>
            </div>
        </>
    );
};

export default BankStatementInputComponent;
